import React, { FC, FormEventHandler, useState } from 'react';
import { Alert, Input } from 'antd';
import { useIntl } from 'react-intl';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import Form, { FormComponentProps } from 'antd/es/form';
import { useDispatch, useSelector } from 'react-redux';
import { Location } from 'history';
import styled from 'styled-components';

import { TestIds } from '@investown/fe/test-utils/testIds';
import { SignInResult, setMfaAuthResult, signIn, AuthErrorCode, sanitizeEmail } from '@investown/fe/common-utils';

import Lang from 'util/IntlMessages';
import {
  APP_DEFAULT_PATH,
  PATH_FORGOTTEN_PASSWORD,
  PATH_SIGN_IN_MFA,
  PATH_INDIVIDUAL_SIGN_UP_EMAIL,
  PATH_LEGAL_ENTITY_SIGN_UP_EMAIL,
} from 'routes/routesPaths';
import { signUpClickedAction } from 'appRedux/onboarding/actions';
import { setFieldError } from 'util/formUtils';
import Button from 'components/Button';
import Split from 'components/Layout/Split';
import HugeHeading from 'components/Typography/HugeHeading';
import SpacerDeprecated from 'components/SpacerDeprecated';
import RightSideCentered from 'components/Layout/Split/RightSideCentered';
import Undraw from 'components/Undraw';
import { UndrawImages } from 'constants/UndrawImages';
import FormLayout from 'components/Layout/FormLayout';
import SpacerHorizontal from 'components/SpacerHorizontal';
import AntdIcon from 'components/AndIcon';
import { isUserSignedIn as isUserSignedInSelector } from 'appRedux/auth/selectors';

const FormItem = Form.Item;

export interface LocationState {
  from?: Location; // user was redirected from path
  successAlertMessage?: string;
}

const SignIn: FC<FormComponentProps> = ({ form }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const isUserSignedIn = useSelector(isUserSignedInSelector);

  const handleAuthError = (authErr: { code?: AuthErrorCode } & Error): void => {
    if (authErr.code && Object.values(AuthErrorCode).includes(authErr.code)) {
      switch (authErr.code) {
        case AuthErrorCode.NotAuthorizedException:
        case AuthErrorCode.UserNotFoundException:
          setFieldError('password', 'signIn.error.invalidEmailOrPassword', intl, form);
          break;
        case AuthErrorCode.PasswordResetRequiredException:
          // TODO: forgot password implementation
          throw new Error('Not implemented');
        case AuthErrorCode.UserNotConfirmedException:
          setFieldError('email', 'signIn.error.userNotConfirmed', intl, form);
          // TODO: The error happens when the password is reset in the Cognito console
          // In this case you need to call forgotPassword to reset the password
          break;
        case AuthErrorCode.UserLambdaValidationException:
          setFieldError('password', 'signIn.error.contactSupport', intl, form);
          break;
        default:
          throw new Error(`Unexpected auth error code: "${authErr.code}"`);
      }
    } else {
      console.error(authErr);
    }
  };

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    form.validateFields(async (err, values) => {
      if (!err) {
        try {
          setLoading(true);
          const { email, password } = values;

          const authResult: SignInResult = await signIn(sanitizeEmail(email), password);

          // MFA
          if (authResult.challengeName) {
            if (authResult.challengeName === 'SMS_MFA') {
              setMfaAuthResult(authResult);
              history.push(PATH_SIGN_IN_MFA);
            } else {
              throw new Error(`${authResult.challengeName} challenge not implemented`);
            }
          }
        } catch (authErr) {
          setLoading(false);
          handleAuthError(authErr as Error);
        }
      }
    });
  };

  const registrationLinkClick = () => {
    dispatch(signUpClickedAction());
  };

  const legalEntityRegistrationLinkClick = () => {
    dispatch(signUpClickedAction());
  };

  if (isUserSignedIn) {
    return <Redirect to={(location.state as LocationState)?.from || APP_DEFAULT_PATH} />;
  }

  return (
    <Split
      leftContent={
        <FormLayout>
          {(location.state as LocationState)?.successAlertMessage && (
            <Alert
              message={(location.state as LocationState)?.successAlertMessage}
              data-testid={TestIds.SignOutSuccessfulLogoutAlert}
              type="success"
              showIcon
            />
          )}
          <HugeHeading data-testid={TestIds.SignInTitle}>
            <Lang id="signIn.heading" />
          </HugeHeading>
          <SpacerDeprecated height="extraLarge" />
          <Form onSubmit={handleSubmit} className="gx-login-form gx-form-row0" data-clarity-mask="true">
            <FormItem>
              {form.getFieldDecorator('email', {
                rules: [{ required: true, message: intl.formatMessage({ id: 'global.validation.missingEmail' }) }],
              })(
                <Input
                  type="email"
                  prefix={<AntdIcon type="mail" style={{ color: 'rgba(0,0,0,.25)' }} />}
                  placeholder={intl.formatMessage({ id: 'global.emailAddress' })}
                  autoFocus
                  autoComplete="off"
                  name="username"
                  data-testid={TestIds.SignInUsername}
                />
              )}
            </FormItem>
            <SpacerDeprecated height="extraLarge" />
            <FormItem>
              {form.getFieldDecorator('password', {
                rules: [{ required: true, message: intl.formatMessage({ id: 'global.validation.missingPassword' }) }],
              })(
                <Input
                  type="password"
                  prefix={<AntdIcon type="lock" style={{ color: 'rgba(0,0,0,.25)' }} />}
                  placeholder={intl.formatMessage({ id: 'global.password' })}
                  data-testid={TestIds.SignInPassword}
                />
              )}
            </FormItem>
            <SpacerDeprecated height="medium" />
            <LinkFormItem>
              <Link
                className="login-form-forgot"
                to={PATH_FORGOTTEN_PASSWORD}
                data-testid={TestIds.SignInForgottenPassword}
              >
                <Lang id="signIn.forgotPassword" />
              </Link>
            </LinkFormItem>
            <SpacerDeprecated height="medium" />
            <FormItem className="gx-text-center">
              <Button
                color="primary"
                htmlType="submit"
                block
                loading={loading}
                data-testid={TestIds.SignInConfirmButton}
              >
                <Lang id="signIn.button" />
              </Button>
            </FormItem>
          </Form>
          <SignUpButtons>
            <Link
              to={PATH_INDIVIDUAL_SIGN_UP_EMAIL}
              onClick={registrationLinkClick}
              data-testid={TestIds.SignInSignUpButton}
            >
              <Lang id="signIn.signUpButton" />
            </Link>
            <SpacerHorizontal width="medium" />
            |
            <SpacerHorizontal width="medium" />
            <Link
              to={PATH_LEGAL_ENTITY_SIGN_UP_EMAIL}
              onClick={legalEntityRegistrationLinkClick}
              data-testid={TestIds.LegalEntitySignInSignUpButton}
            >
              <Lang id="signIn.legalEntitySignUpButton" />
            </Link>
          </SignUpButtons>
        </FormLayout>
      }
      rightContent={
        <RightSideCentered>
          <Undraw name={UndrawImages.Data} />
        </RightSideCentered>
      }
    />
  );
};

const SignUpButtons = styled.div`
  position: absolute;
  display: flex;
  top: ${({ theme }) => theme.margins.huge};
  right: ${({ theme }) => theme.margins.huge};
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.small}) {
    top: ${({ theme }) => theme.margins.large};
    right: ${({ theme }) => theme.margins.large};
    font-size: ${({ theme }) => theme.font.size.mini};
  }
`;

const LinkFormItem = styled(FormItem)`
  .ant-form-item-control {
    line-height: 20px;
  }
  font-size: ${({ theme }) => theme.font.size.mini};
`;

const WrappedNormalSignUpForm = Form.create()(SignIn);

export default WrappedNormalSignUpForm;
